import * as React from 'react';
import { StepShell, Card, Button, FieldError } from '../../components/ui';
import Select from '../../components/select.component';
import LookingFor from '../../components/looking-for';
import useFieldLift from '../../hooks/use-field-lift';
import {
  REQUIRED_FOR_DELIVERY, POSTAL_PATTERNS, countryOptions, regionOptions,
} from '../../config';
import { maskEmail, trimEmailTail, correctEmail, emailSuggestion, isValidEmail } from '../../utils/email';
import { maskPhone, normalisePhone } from '../../utils/phone';
import { maskPostal, restoreZipHyphenLeadingZero, tidyZip } from '../../utils/postal';
import { parseAddressComponents, cityFromPrediction } from '../../utils/address';
import sizeGuideBody from '../../imgs/size-guide-body.png';

// Same key the legacy /checkout already uses in production (checkout.page.jsx:266).
const GOOGLE_MAPS_API_KEY = 'AIzaSyCJl-aJxA49ksIx-X_m90GcEMUJJrhoyik';

// [letter, US size, chest range] — read directly off the design's own size
// table (STEP 3 OF 4 file), both the modal's table and the on-page chips.
const SIZE_ROWS = [
  ['S', '2–4', '32–35'],
  ['M', '6–8', '36–38'],
  ['L', '10–12', '40–42'],
  ['XL', '14–16', '44–46'],
  ['2XL', '18–20', '48–50'],
  ['3XL', '22–24', '52–54'],
];

// Autofill tokens by path (STEP 3 handoff note). "For me" gets the real tokens
// so Chrome/Safari fill the saved address. The gift path gets deliberately
// non-standard "mtm-nofill-*" tokens: autocomplete="off" is ignored by Chrome
// on address-looking fields, so it would still fill the BUYER's saved address
// into the RECIPIENT's — an unknown token keeps the field out of the address
// group entirely. Email and mobile stay standard on both paths (they are
// always the buyer's). Do not "fix" the gift tokens to standard ones.
const AUTOCOMPLETE = {
  self: {
    first: 'given-name', last: 'family-name', street: 'address-line1', apt: 'address-line2',
    city: 'address-level2', postal: 'postal-code', region: 'address-level1', country: 'country',
  },
  gift: {
    first: 'mtm-nofill-first', last: 'mtm-nofill-last', street: 'mtm-nofill-street', apt: 'mtm-nofill-apt',
    city: 'mtm-nofill-city', postal: 'mtm-nofill-postal', region: 'mtm-nofill-region', country: 'mtm-nofill-country',
  },
};

// Chrome also guesses a field's purpose from its name/id, so on the gift path
// the address inputs carry neither (data-field is what this code looks up).
const norm = (v) => String(v || '').toLowerCase().replace(/\./g, '').replace(/\s+/g, ' ').trim();

// 1px, opacity 0, tabIndex -1 — NOT display:none, visibility:hidden or
// zero-size, or Chrome/Safari skip them and never fill them.
const PROXY_STYLE = {
  position: 'absolute', left: 0, top: 0, width: 1, height: 1, opacity: 0, pointerEvents: 'none', border: 0, padding: 0,
};

// Google Places (New) API — same approach and loader snippet as the legacy
// /checkout (checkout.page.jsx:276-304), not the classic AutocompleteService/
// PlacesService pair (deprecated by Google, March 2025).
async function initGooglePlaces() {
  if (!window.google?.maps?.importLibrary) {
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) existingScript.remove();
    if (window.google?.maps) delete window.google.maps;
    const g = { key: GOOGLE_MAPS_API_KEY, v: 'weekly' };
    /* eslint-disable no-sequences, no-unused-expressions */
    ((g) => { var h, a, k, p = 'The Google Maps JavaScript API', c = 'google', l = 'importLibrary', q = '__ib__', m = document, b = window; b = b[c] || (b[c] = {}); var d = b.maps || (b.maps = {}), r = new Set(), e = new URLSearchParams(), u = () => h || (h = new Promise(async (f, n) => { await (a = m.createElement('script')); e.set('libraries', [...r] + ''); for (k in g) e.set(k.replace(/[A-Z]/g, (t) => '_' + t[0].toLowerCase()), g[k]); e.set('callback', c + '.maps.' + q); a.src = `https://maps.${c}apis.com/maps/api/js?` + e; d[q] = f; a.onerror = () => h = n(Error(p + ' could not load.')); a.nonce = m.querySelector('script[nonce]')?.nonce || ''; m.head.append(a); })); d[l] ? console.warn(p + ' only loads once. Ignoring:', g) : d[l] = (f, ...n) => r.add(f) && u().then(() => d[l](f, ...n)); })(g);
    /* eslint-enable no-sequences, no-unused-expressions */
  }
  const { AutocompleteSuggestion, AutocompleteSessionToken } = await window.google.maps.importLibrary('places');
  return { AutocompleteSuggestion, sessionToken: new AutocompleteSessionToken() };
}

// Size guide dialog, as the design draws it: a body diagram, the size table
// (each row picks that size, and the chosen size's row is picked out so the
// chart confirms the pick), and a dark Close button. Backdrop click, Escape
// and the X all close it; a click inside the dialog does not.
function SizeGuideModal({ open, onClose, onPick, selected }) {
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 80, background: 'rgba(35, 31, 30, 0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }}
    >
      <div role="dialog" aria-modal="true" aria-labelledby="size-guide-title" onClick={(e) => e.stopPropagation()} style={{
        width: '100%', maxWidth: 450, maxHeight: 'calc(100vh - 32px)', overflowY: 'auto',
        background: '#FFFFFF', borderRadius: 14, padding: '20px 20px 22px', boxShadow: '0 18px 44px rgba(35, 31, 30, 0.28)',
        color: 'var(--ec-text)', animation: 'ecRise 0.2s ease-out',
      }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div id="size-guide-title" style={{ fontSize: 19, fontWeight: 500, letterSpacing: '0.01em' }}>Size guide</div>
          <button type="button" aria-label="Close size guide" onClick={onClose} className="ec-size-x">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3.5 3.5l9 9M12.5 3.5l-9 9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
          </button>
        </div>
        <img src={sizeGuideBody} alt="Where to measure: chest, waist, length and hips" width={743} height={840} decoding="async"
          style={{ display: 'block', width: '100%', maxWidth: 220, height: 'auto', aspectRatio: '743 / 840', margin: '14px auto 0' }} />
        <table style={{ width: '100%', marginTop: 14, borderCollapse: 'collapse', fontSize: 15, lineHeight: 1.35, textAlign: 'left' }}>
          <thead>
            <tr>
              <th style={{ padding: '8px 8px 8px 10px', borderBottom: '1px solid var(--ec-border)', fontSize: 14, fontWeight: 500, color: 'var(--ec-label)' }}>Size</th>
              <th style={{ padding: 8, borderBottom: '1px solid var(--ec-border)', fontSize: 14, fontWeight: 500, color: 'var(--ec-label)' }}>US size</th>
              <th style={{ padding: '8px 10px 8px 8px', borderBottom: '1px solid var(--ec-border)', fontSize: 14, fontWeight: 500, color: 'var(--ec-label)' }}>Chest (inches)</th>
            </tr>
          </thead>
          <tbody>
            {SIZE_ROWS.map(([sz, us, chest]) => (
              <tr key={sz} role="button" tabIndex={0} aria-label={`Choose size ${sz}`} className={selected === sz ? 'ec-size-row is-selected' : 'ec-size-row'} onClick={() => onPick(sz)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPick(sz); } }}>
                <td style={{ padding: '9px 8px 9px 10px', borderBottom: '1px solid var(--ec-border)', fontWeight: 500 }}>{sz}</td>
                <td style={{ padding: '9px 8px', borderBottom: '1px solid var(--ec-border)' }}>{us}</td>
                <td style={{ padding: '9px 10px 9px 8px', borderBottom: '1px solid var(--ec-border)' }}>{chest}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <button type="button" onClick={onClose} className="ec-cta" style={{ display: 'block', width: '100%', minHeight: 48, marginTop: 18 }}>
          Close
        </button>
      </div>
    </div>
  );
}

const EMAIL_FIELDS = ['email', 'giftEmail'];

export default function Step3Delivery({ config, state, patch, onNext, onEvent }) {
  const [errors, setErrors] = React.useState({});
  const [fixed, setFixed] = React.useState({});
  const [giftOpen, setGiftOpen] = React.useState(!!state.giftMessage || !!state.giftEmail);
  const [sizeGuideOpen, setSizeGuideOpen] = React.useState(false);
  const [predictions, setPredictions] = React.useState([]);
  // Which email field is focused gates whether it offers a completion; dismissed
  // tracks an Escape (or leaving the field) until the next keystroke.
  const [focusedEmail, setFocusedEmail] = React.useState('');
  const [suggestDismissed, setSuggestDismissed] = React.useState({});
  const [placesUp, setPlacesUp] = React.useState(true);
  const placesRef = React.useRef(null); // { AutocompleteSuggestion, sessionToken }
  const debounceRef = React.useRef(null);
  const rootRef = React.useRef(null);
  const streetWrapRef = React.useRef(null);
  const placesList = React.useRef(null);
  const placesLocked = React.useRef(false);
  const menuPressedAt = React.useRef(0);
  const pendingCaret = React.useRef(null);
  const gift = state.audience === 'gift';
  const country = state.country || 'US';
  const ac = gift ? AUTOCOMPLETE.gift : AUTOCOMPLETE.self;
  // id/name only on the self path (see note above AUTOCOMPLETE); data-field
  // is the lookup key on both.
  const attrs = (key) => (gift ? { 'data-field': key } : { 'data-field': key, id: key, name: key });

  useFieldLift(rootRef);

  React.useEffect(() => {
    initGooglePlaces().then((p) => { placesRef.current = p; }).catch((err) => {
      console.error('Google Places failed to load:', err);
    });
  }, []);

  // ---- what a field is called in its own error ("Enter first name") ---------
  const nameOf = (key) => ({
    first: 'first name', last: 'last name', street: 'street address', city: 'city',
    region: country === 'CA' ? 'province' : 'state', postal: country === 'CA' ? 'postal code' : 'ZIP code',
    email: 'email address', giftEmail: 'recipient email',
  }[key] || key);

  // Missing and wrong are told apart ("Enter ZIP code" vs "Enter a valid ZIP
  // code"); sentence case, no "Please", no period (design).
  const errorForValue = (key, raw) => {
    const v = String(raw || '').trim();
    if (!v) return REQUIRED_FOR_DELIVERY.includes(key) ? `${key === 'region' ? 'Select' : 'Enter'} ${nameOf(key)}` : '';
    if (EMAIL_FIELDS.includes(key) && !isValidEmail(v)) return 'Enter a valid email address';
    if (key === 'postal') {
      const re = POSTAL_PATTERNS[country];
      if (re && !re.test(v)) return `Enter a valid ${nameOf('postal')}`;
    }
    return '';
  };
  const errorFor = (key) => errorForValue(key, state[key]);

  // ---- address suggestions --------------------------------------------------
  // Opens upward when the room above the field (below the sticky header,
  // inside the visual viewport) fits a full list (four rows and the logo),
  // downward otherwise. Decided once per opening and kept until it closes.
  const setPlacesUl = React.useCallback((el) => {
    placesList.current = el;
    if (!el) { placesLocked.current = false; return; }
    requestAnimationFrame(() => {
      const input = streetWrapRef.current && streetWrapRef.current.querySelector('input');
      if (!placesList.current || !input || placesLocked.current) return;
      placesLocked.current = true;
      const vv = window.visualViewport;
      const head = document.querySelector('.ec-header');
      const visTop = (vv ? vv.offsetTop : 0) + (head ? Math.max(0, head.getBoundingClientRect().bottom) : 0);
      const need = Math.max(placesList.current.offsetHeight, 12 + 4 * 48 + 4 * 6 + 26);
      setPlacesUp(input.getBoundingClientRect().top - 6 - visTop >= need);
    });
  }, []);

  const onStreetChange = (e) => {
    const input = e.target.value;
    patch({ street: input });
    if (errors.street) setErrors((x) => ({ ...x, street: '' }));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!input || input.length < 3 || !placesRef.current) { setPredictions([]); return; }
    debounceRef.current = setTimeout(async () => {
      try {
        const { AutocompleteSuggestion, sessionToken } = placesRef.current;
        const response = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input,
          includedRegionCodes: [country.toLowerCase()],
          sessionToken,
        });
        setPredictions((response?.suggestions || []).map((x) => x.placePrediction).slice(0, 4));
      } catch (err) {
        console.error('Autocomplete suggestions error:', err);
      }
    }, 300);
  };

  const pickPlace = async (placePrediction) => {
    setPredictions([]);
    try {
      const place = placePrediction.toPlace();
      await place.fetchFields({ fields: ['addressComponents'] });
      const components = place.addressComponents.map((c) => ({
        long_name: c.longText, short_name: c.shortText, types: c.types,
      }));
      const parsed = parseAddressComponents(components);
      patch({
        street: parsed.street,
        city: parsed.city || cityFromPrediction(placePrediction, parsed.regionCode),
        region: parsed.regionCode,
        postal: parsed.zip,
      });
      setErrors((x) => ({ ...x, street: '', city: '', region: '', postal: '' }));
      const { AutocompleteSessionToken } = await window.google.maps.importLibrary('places');
      if (placesRef.current) placesRef.current.sessionToken = new AutocompleteSessionToken();
    } catch (err) {
      console.error('Place details error:', err);
    }
  };

  // The field's blur closes the list at once, unless a press started inside the
  // list a moment ago (some mobile browsers blur anyway); then the close waits
  // so the click still lands.
  const closePlacesSoon = () => {
    const closeIt = () => setPredictions([]);
    if (Date.now() - menuPressedAt.current < 800) setTimeout(closeIt, 350); else closeIt();
  };

  // A country change made by the CUSTOMER invalidates region, city AND postal:
  // they are a different namespace, not the same value in a new country.
  const onCountryChange = (e) => {
    setErrors((x) => ({ ...x, region: '', city: '', postal: '' }));
    patch({
      country: e.target.value, region: '', city: '', postal: '',
    });
  };

  // State/Province and Country are custom menus, which browser autofill can't
  // reach. Hidden address-level1 / country inputs (data-fill proxies) catch
  // the filled value and copy it into the menus (STEP 3 handoff note). Region
  // lookup covers BOTH countries, so an Ontario profile lands correctly even
  // while the country menu still reads US.
  const stateRef = React.useRef(state);
  stateRef.current = state;
  const fillRegion = React.useCallback((raw) => {
    const n = norm(raw);
    if (!n) return;
    const cur = stateRef.current;
    const find = (cc) => regionOptions(cc).find((o) => norm(o.value) === n || norm(o.label) === n);
    const us = find('US');
    const ca = find('CA');
    const hit = (cur.country || 'US') === 'CA' ? (ca || us) : (us || ca);
    if (!hit) return;
    const cc = hit === ca ? 'CA' : 'US';
    if (hit.value === cur.region && cc === (cur.country || 'US')) return;
    patch({ region: hit.value, country: cc });
    setErrors((x) => ({ ...x, region: '' }));
  }, [patch]);
  const fillCountry = React.useCallback((raw) => {
    const n = norm(raw);
    const cc = ['us', 'usa', 'united states', 'united states of america'].includes(n) ? 'US'
      : ['ca', 'can', 'canada'].includes(n) ? 'CA' : '';
    if (!cc) return;
    const cur = stateRef.current;
    if (cc === (cur.country || 'US')) return;
    const keep = regionOptions(cc).some((o) => o.value === cur.region);
    patch({ country: cc, region: keep ? cur.region : '' });
  }, [patch]);

  // Chrome/iOS often fill without firing change events until the user
  // interacts, so the form is also swept shortly after mount: any input whose
  // DOM value differs from state is adopted, and the two proxies are read.
  const adoptAutofill = React.useCallback(() => {
    const root = rootRef.current;
    if (!root) return;
    const cur = stateRef.current;
    const next = {};
    root.querySelectorAll('input[data-field]').forEach((el) => {
      const key = el.getAttribute('data-field');
      if (el.value && el.value !== (cur[key] || '')) next[key] = el.value;
    });
    if (Object.keys(next).length) patch(next);
    const c = root.querySelector('input[data-fill="country"]');
    const r = root.querySelector('input[data-fill="region"]');
    if (c && c.value) fillCountry(c.value);
    if (r && r.value) fillRegion(r.value);
  }, [patch, fillCountry, fillRegion]);
  React.useEffect(() => {
    const timers = [120, 800].map((ms) => setTimeout(adoptAutofill, ms));
    return () => timers.forEach(clearTimeout);
  }, [adoptAutofill]);

  // Restoring the caret has to happen after React has written the masked value
  // to the DOM, which a layout effect guarantees (it runs before paint).
  React.useLayoutEffect(() => {
    const pending = pendingCaret.current;
    if (!pending) return;
    pendingCaret.current = null;
    const el = rootRef.current && rootRef.current.querySelector(`[data-field="${pending.field}"]`);
    if (!el || document.activeElement !== el) return;
    try { el.setSelectionRange(pending.pos, pending.pos); } catch { /* not selectable */ }
  });

  // One change handler: masks what may be typed, keeps the caret where it was,
  // and clears the field's own error (an email's only once it is valid).
  const set = (field) => (e) => {
    const isEmail = EMAIL_FIELDS.includes(field);
    const raw = e.target.value;
    let value = raw;
    if (isEmail) value = maskEmail(raw);
    else if (field === 'phone') value = maskPhone(raw);
    else if (field === 'postal') {
      // Autofill and paste arrive as one jump, not a keystroke: masked by the
      // shape of what came in, not by the country menu (a browser can't fill
      // our custom menu, so an autofilled Canadian code under a still-US
      // country would otherwise lose its letters).
      const bulk = Math.abs(raw.length - String(state.postal || '').length) > 1;
      const caPostal = bulk ? /[A-Za-z]/.test(raw) : country === 'CA';
      value = restoreZipHyphenLeadingZero(raw, maskPostal(raw, caPostal), caPostal);
    }
    if (raw !== value) {
      pendingCaret.current = { field, pos: Math.max(0, (e.target.selectionStart || 0) - (raw.length - value.length)) };
    }
    patch({ [field]: value });
    if (fixed[field]) setFixed((x) => ({ ...x, [field]: '' }));
    if (isEmail && suggestDismissed[field]) setSuggestDismissed((x) => ({ ...x, [field]: false }));
    if (errors[field] && (!isEmail || isValidEmail(value))) setErrors((x) => ({ ...x, [field]: '' }));
  };

  // Required fields are judged when the customer leaves them.
  const blur = (field) => () => {
    if (!REQUIRED_FOR_DELIVERY.includes(field)) return;
    let v = state[field];
    if (field === 'postal') {
      // Judged after tidying: "10080-" commits as 10080 instead of erroring.
      const tidied = tidyZip(state.postal, country === 'CA');
      if (tidied !== state.postal) { patch({ postal: tidied }); v = tidied; }
    }
    setErrors((x) => ({ ...x, [field]: errorForValue(field, v) }));
  };

  // Optional, so it only ever raises an error and never blocks Continue alone.
  const blurPhone = () => {
    const v = (state.phone || '').trim();
    setErrors((x) => ({ ...x, phone: v && !normalisePhone(v) ? 'Enter a valid 10-digit mobile number, or leave blank' : '' }));
  };

  // Blur finishes an address off: trim a trailing separator, fix a misspelt
  // domain, and only then judge it, so a correctable typo never shows an error.
  // The recipient's email is optional: it is tidied the same way but never
  // flagged just for being incomplete.
  const blurEmail = (field) => () => {
    setSuggestDismissed((x) => ({ ...x, [field]: true }));
    setFocusedEmail('');
    const before = state[field] || '';
    let value = trimEmailTail(before);
    const corrected = correctEmail(value, country);
    if (corrected) value = corrected;
    if (value !== before) patch({ [field]: value });
    setErrors((x) => ({
      ...x,
      [field]: field === 'email' && value && !isValidEmail(value) ? 'Enter a valid email address' : '',
    }));
    setFixed((x) => ({ ...x, [field]: corrected ? `Corrected to ${corrected}` : '' }));
    if (field === 'email' && value && isValidEmail(value) && onEvent) onEvent('email-confirmed', { email: value });
  };

  const emailSug = (field) => (
    focusedEmail === field && !suggestDismissed[field] ? emailSuggestion(state[field], country) : ''
  );
  const acceptSuggestion = (field) => {
    const sug = emailSug(field);
    if (!sug) return;
    patch({ [field]: sug });
    setErrors((x) => ({ ...x, [field]: '' }));
    const el = rootRef.current && rootRef.current.querySelector(`[data-field="${field}"]`);
    if (el) el.focus({ preventScroll: true });
  };
  // Tab and Right-arrow accept, as inline completions do elsewhere; Escape
  // dismisses without changing the value.
  const emailKeyDown = (field) => (e) => {
    if (!emailSug(field)) return;
    if (e.key === 'Escape') { setSuggestDismissed((x) => ({ ...x, [field]: true })); return; }
    const atEnd = e.target.selectionStart === (state[field] || '').length;
    if (e.key === 'Tab' || (e.key === 'ArrowRight' && atEnd)) { e.preventDefault(); acceptSuggestion(field); }
  };

  const closeGift = () => {
    setGiftOpen(false);
    patch({ giftMessage: '', giftEmail: '' });
    setErrors((x) => ({ ...x, giftEmail: '' }));
    setFixed((x) => ({ ...x, giftEmail: '' }));
  };

  const submit = () => {
    // Autofill and paste may never blur the ZIP, so tidy it here too and judge
    // the tidied value rather than the stale one.
    const zip = tidyZip(state.postal, country === 'CA');
    if (zip !== state.postal) patch({ postal: zip });
    const next = {};
    REQUIRED_FOR_DELIVERY.forEach((k) => {
      const msg = k === 'postal' ? errorForValue('postal', zip) : errorFor(k);
      if (msg) next[k] = msg;
    });
    // Optional, so empty is fine, but a junk number or address must not pass.
    const rawPhone = (state.phone || '').trim();
    if (rawPhone && !normalisePhone(rawPhone)) next.phone = 'Enter a valid 10-digit mobile number, or leave blank';
    if (gift && giftOpen && state.giftEmail && !isValidEmail(state.giftEmail)) next.giftEmail = 'Enter a valid email address';
    setErrors(next);
    const keys = REQUIRED_FOR_DELIVERY.concat('phone', 'giftEmail');
    const firstBad = keys.find((k) => next[k]);
    if (firstBad) {
      const el = rootRef.current && (rootRef.current.querySelector(`[data-field="${firstBad}"]`)
        || rootRef.current.querySelector(`[data-select="${firstBad}"] button`));
      if (el) { el.focus({ preventScroll: true }); const r = el.getBoundingClientRect(); window.scrollTo({ top: window.scrollY + r.top - 120, behavior: 'smooth' }); }
      return;
    }
    if (onEvent) onEvent('email-confirmed', { email: state.email });
    if (onEvent) onEvent('delivery-complete');
    onNext();
  };

  // A street with no digit is probably missing its house number: a nudge, not
  // an error, so it never blocks Continue.
  const streetWarn = !errors.street && (state.street || '').trim() && !/\d/.test(state.street)
    ? 'Your address appears to be missing a house or building number, like: 42 Elm Street.' : '';

  const text = (key, label, extra = {}) => (
    <div style={{ minWidth: 0 }}>
      <div className={`ec-field ${state[key] ? 'has-value' : ''} ${errors[key] ? 'has-error' : ''}`}>
        <input {...attrs(key)} className="ec-field-control" placeholder=" " value={state[key] || ''} onChange={set(key)}
          onBlur={blur(key)} aria-invalid={errors[key] ? 'true' : 'false'} aria-describedby={`ec-err-${key}`} {...extra} />
        <span className="ec-field-label">{label}</span>
      </div>
      <FieldError id={`ec-err-${key}`} message={errors[key]} />
    </div>
  );

  // ---- gift block -----------------------------------------------------------
  const giftBlock = !gift ? null : (
    <div style={{ marginTop: 18 }}>
      {!giftOpen ? (
        <button type="button" onClick={() => setGiftOpen(true)} className="ec-addgift">
          <span>+ Add a gift message</span>
        </button>
      ) : (
        <div style={{ animation: 'ecRise 0.22s ease-out' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ fontSize: 19, fontWeight: 500, letterSpacing: '0.01em' }}>Gift message</div>
            <button type="button" onClick={closeGift} className="ec-gift-remove">Remove</button>
          </div>
          <div style={{ fontSize: 15, lineHeight: 1.5, color: 'var(--ec-label)', fontWeight: 400, marginTop: 6 }}>
            The recipient receives an email with the gift message when the first box ships. Boxes never contain prices or invoices.
          </div>
          <div className="ec-stack">
            <div style={{ position: 'relative' }}>
              <div className={`ec-field ${state.giftEmail ? 'has-value' : ''} ${errors.giftEmail ? 'has-error' : ''}`}>
                {/* Non-standard token, deliberately: neither "email" (would offer
                    the shopper's OWN saved email) nor "off" (Chrome ignores it). */}
                <input data-field="giftEmail" name="mtm-recipient-email-field" type="email" inputMode="email" autoCapitalize="none"
                  autoCorrect="off" spellCheck={false} maxLength={254} className="ec-field-control" placeholder=" "
                  autoComplete="mtm-nofill-recipient-email" data-lpignore="true" data-1p-ignore="true" data-form-type="other"
                  value={state.giftEmail || ''} onChange={set('giftEmail')} onFocus={() => setFocusedEmail('giftEmail')}
                  onBlur={blurEmail('giftEmail')} onKeyDown={emailKeyDown('giftEmail')}
                  aria-invalid={errors.giftEmail ? 'true' : 'false'} aria-describedby="ec-err-giftEmail" />
                <span className="ec-field-label">Recipient email</span>
              </div>
              <FieldError id="ec-err-giftEmail" message={errors.giftEmail} />
              {emailSug('giftEmail') && (
                <ul className="ec-suggest" role="listbox" aria-label="Email suggestions">
                  <li className="ec-suggest-row" role="option" aria-selected="false" onMouseDown={(e) => e.preventDefault()} onClick={() => acceptSuggestion('giftEmail')}>
                    <span className="ec-suggest-pill">Tap to use</span>
                    <span className="ec-suggest-text">{emailSug('giftEmail')}</span>
                  </li>
                </ul>
              )}
              {fixed.giftEmail && <div className="ec-fixed"><span style={{ flex: '0 0 auto' }}>✓</span><span>{fixed.giftEmail}</span></div>}
            </div>
            <div className={`ec-field ec-field--textarea ${state.giftMessage ? 'has-value' : ''}`}>
              <textarea rows={4} maxLength={200} className="ec-field-control" placeholder=" " value={state.giftMessage || ''}
                onChange={(e) => patch({ giftMessage: e.target.value.slice(0, 200) })} />
              <span className="ec-field-label">Message</span>
            </div>
            <div style={{ fontSize: 15, color: 'var(--ec-label)', fontWeight: 400, marginTop: -4 }}>{(state.giftMessage || '').length} / 200 characters</div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <StepShell stepLabel={config.copy.step3Label || 'STEP 3 OF 4: DELIVERY DETAILS'}>
      <div ref={rootRef}>
        <Card title="Shipping address" subtitle="Recipient’s name and delivery address">
          <div className="ec-stack">
            <div className="ec-two-up">
              {text('first', 'First Name', { type: 'text', autoComplete: ac.first })}
              {text('last', 'Last Name', { type: 'text', autoComplete: ac.last })}
            </div>

            <Select id="country" label="Country" options={countryOptions()} value={country} onChange={onCountryChange} clearable={false} />

            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 10 }}>
              <div>
                <div ref={streetWrapRef} style={{ position: 'relative', minWidth: 0 }}>
                  <div className={`ec-field ${state.street ? 'has-value' : ''} ${errors.street ? 'has-error' : ''}`}>
                    <input {...attrs('street')} type="text" className="ec-field-control" placeholder=" " autoComplete={ac.street}
                      value={state.street || ''} onChange={onStreetChange}
                      onBlur={() => { closePlacesSoon(); blur('street')(); }}
                      aria-invalid={errors.street ? 'true' : 'false'} aria-describedby="ec-err-street"
                      style={!(state.street || '').trim() ? { paddingRight: 44 } : undefined} />
                    <span className="ec-field-label">Street Address</span>
                  </div>
                  {/* Marks the field as autocomplete-backed while it is still
                      a search (empty), like a search box that drops its icon
                      once there is a query. */}
                  {!(state.street || '').trim() && (
                    <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="#5F5F5F" strokeWidth="1.8" strokeLinecap="round"
                      style={{ position: 'absolute', right: 14, top: 28.5, transform: 'translateY(-50%)', width: 20, height: 20, pointerEvents: 'none' }}>
                      <circle cx="10.5" cy="10.5" r="6.5" /><line x1="15.4" y1="15.4" x2="20" y2="20" />
                    </svg>
                  )}
                  {predictions.length > 0 && (
                    <ul className="ec-places" ref={setPlacesUl} role="listbox" aria-label="Address suggestions"
                      onPointerDown={() => { menuPressedAt.current = Date.now(); }}
                      onMouseDown={(e) => e.preventDefault()}
                      style={placesUp ? { bottom: 'calc(100% + 6px)', top: 'auto' } : { top: 'calc(100% + 6px)', bottom: 'auto' }}>
                      {/* Required by Google's terms when not using their own widget;
                          on the side of the list farthest from the field. */}
                      {placesUp && (
                        <li className="ec-places-logo" style={{ padding: '4px 14px 8px' }}>
                          <img src="//maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3.png" alt="Powered by Google" />
                        </li>
                      )}
                      {predictions.map((p, i) => (
                        <li key={i} role="option" aria-selected="false" className="ec-places-row" onClick={() => pickPlace(p)}>{p.text.toString()}</li>
                      ))}
                      {!placesUp && (
                        <li className="ec-places-logo" style={{ padding: '8px 14px 4px' }}>
                          <img src="//maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3.png" alt="Powered by Google" />
                        </li>
                      )}
                    </ul>
                  )}
                </div>
                <FieldError id="ec-err-street" message={errors.street} />
                {streetWarn && (
                  <div className="ec-streetwarn"><span style={{ flex: '0 0 auto', lineHeight: 1.4 }}>ⓘ</span><span>{streetWarn}</span></div>
                )}
              </div>
              <div className={`ec-field ${state.apt ? 'has-value' : ''}`} style={{ minWidth: 0 }}>
                <input {...attrs('apt')} type="text" className="ec-field-control" placeholder=" " autoComplete={ac.apt}
                  value={state.apt || ''} onChange={set('apt')} />
                <span className="ec-field-label">Apartment, suite, etc. (optional)</span>
              </div>
            </div>

            {text('city', 'City', { type: 'text', autoComplete: ac.city })}

            <div style={{ position: 'relative' }} className="ec-two-up">
              <input type="text" data-fill="country" autoComplete={ac.country} tabIndex={-1} aria-hidden="true" style={PROXY_STYLE}
                onChange={(e) => fillCountry(e.target.value)} />
              <input type="text" data-fill="region" autoComplete={ac.region} tabIndex={-1} aria-hidden="true" style={PROXY_STYLE}
                onChange={(e) => fillRegion(e.target.value)} />
              <div style={{ minWidth: 0 }}>
                <Select id="region" label={country === 'CA' ? 'Province' : 'State'} options={regionOptions(country)} value={state.region || ''}
                  error={null} clearable={false} wideMenu
                  onChange={(e) => { patch({ region: e.target.value }); if (errors.region) setErrors((x) => ({ ...x, region: '' })); }} />
                <FieldError id="ec-err-region" message={errors.region} />
              </div>
              {text('postal', country === 'CA' ? 'Postal Code' : 'ZIP Code', {
                type: 'text', autoComplete: ac.postal, inputMode: country === 'CA' ? 'text' : 'numeric',
                pattern: country === 'CA' ? undefined : '[0-9]*', autoCapitalize: country === 'CA' ? 'characters' : 'none',
                autoCorrect: 'off', spellCheck: false,
              })}
            </div>
          </div>

          {giftBlock}
        </Card>

        <Card title="T-shirt size">
          {/* "(Optional) Can be updated later · Size guide": one wrapping row,
              the link led by a small ruler (design). */}
          <div style={{ display: 'flex', flexWrap: 'wrap', columnGap: 6, fontSize: 15, lineHeight: 1.5, color: 'var(--ec-label)', fontWeight: 400, marginTop: 6 }}>
            <span>(Optional) Can be updated later &middot;</span>
            <button type="button" aria-haspopup="dialog" onClick={() => setSizeGuideOpen(true)} className="ec-size-link">
              <svg aria-hidden="true" width="18" height="10" viewBox="0 0 18 10" fill="none" style={{ flex: '0 0 auto', color: '#8A8A8A' }}>
                <rect x="0.75" y="0.75" width="16.5" height="8.5" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
                <path d="M4.5 1v3.2M7.5 1v2M10.5 1v3.2M13.5 1v2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
              </svg>
              <span style={{ textDecoration: 'underline', textUnderlineOffset: 3 }}>Size guide</span>
            </button>
          </div>
          <div style={{ marginTop: 18, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
            {SIZE_ROWS.map(([sz]) => {
              const on = state.topSize === sz;
              return (
                <button key={sz} type="button" onClick={() => patch({ topSize: on ? '' : sz })} style={{
                  minHeight: 56, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3,
                  padding: '10px 4px', borderRadius: 10, cursor: 'pointer', fontFamily: 'var(--ec-font)',
                  transition: 'background 0.16s ease, border-color 0.16s ease',
                  background: on ? '#FFF4EE' : '#FFFFFF', border: `2px solid ${on ? '#FDD7C2' : '#D7D7D7'}`,
                }}
                >
                  <span style={{ fontSize: 19, fontWeight: 500, letterSpacing: '0.04em', color: 'var(--ec-text)' }}>{sz}</span>
                </button>
              );
            })}
          </div>
        </Card>

        <SizeGuideModal
          open={sizeGuideOpen}
          onClose={() => setSizeGuideOpen(false)}
          onPick={(sz) => { patch({ topSize: sz }); setSizeGuideOpen(false); }}
          selected={state.topSize}
        />

        {/* Its own card: Email + Mobile live under "Subscription account holder",
            not inside the shipping-address card. */}
        <Card title={gift ? 'Subscription account holder' : 'Your account details'} subtitle="Billing, receipts, tracking & account portal.">
          <div className="ec-stack">
            <div style={{ position: 'relative' }}>
              <div className={`ec-field ${state.email ? 'has-value' : ''} ${errors.email ? 'has-error' : ''}`}>
                <input id="email" name="email" data-field="email" type="email" inputMode="email" autoCapitalize="none" autoCorrect="off"
                  spellCheck={false} maxLength={254} className="ec-field-control" placeholder=" " autoComplete="email"
                  value={state.email || ''} onChange={set('email')} onFocus={() => setFocusedEmail('email')}
                  onBlur={blurEmail('email')} onKeyDown={emailKeyDown('email')}
                  aria-invalid={errors.email ? 'true' : 'false'} aria-describedby="ec-err-email" />
                <span className="ec-field-label">Email Address</span>
              </div>
              <FieldError id="ec-err-email" message={errors.email} />
              {emailSug('email') && (
                <ul className="ec-suggest" role="listbox" aria-label="Email suggestions">
                  <li className="ec-suggest-row" role="option" aria-selected="false" onMouseDown={(e) => e.preventDefault()} onClick={() => acceptSuggestion('email')}>
                    <span className="ec-suggest-pill">Tap to use</span>
                    <span className="ec-suggest-text">{emailSug('email')}</span>
                  </li>
                </ul>
              )}
              {fixed.email && <div className="ec-fixed"><span style={{ flex: '0 0 auto' }}>✓</span><span>{fixed.email}</span></div>}
            </div>
            <div>
              <div className={`ec-field ${state.phone ? 'has-value' : ''} ${errors.phone ? 'has-error' : ''}`}>
                <input id="phone" name="tel" data-field="phone" type="tel" inputMode="tel" autoComplete="tel" autoCorrect="off"
                  spellCheck={false} maxLength={18} className="ec-field-control" placeholder=" " value={state.phone || ''}
                  onChange={set('phone')} onBlur={blurPhone} aria-invalid={errors.phone ? 'true' : 'false'} aria-describedby="ec-err-phone" />
                <span className="ec-field-label">Mobile Number (optional)</span>
              </div>
              <FieldError id="ec-err-phone" message={errors.phone} />
            </div>
          </div>
          <div style={{ marginTop: 14, fontSize: 14, lineHeight: 1.5, color: 'var(--ec-label)', fontWeight: 400 }}>
            By continuing, you agree to our <a href="/privacy-policy" className="ec-link">Privacy &amp; Terms</a>. You may receive shipping updates and automated marketing messages, and can unsubscribe at any time.
          </div>
        </Card>

        <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div className="ec-upnext" style={{ marginBottom: 0 }}>Up next &middot; Review &amp; pay</div>
          <Button text="Continue" onClick={submit} />
        </div>

        <LookingFor step={3} gift={gift} style={{ marginTop: 'max(24px, calc(40vh - 52px))' }} />
      </div>
    </StepShell>
  );
}
